import React from 'react'
import { FormikProps } from 'formik'
import { Text, Flex, Checkbox, Form } from '@sketch/components'
import {
  LibrariesPanelBox,
  LibraryItemBox,
  LibraryPic,
  SearchInput,
  SearchEmptyIcon,
  StickyDiv,
} from './FormEditCreate.styles'
import { ReactComponent as WarningTriangle } from '@sketch/icons/warning-triangle-64-svg'
import { FormProps, Library } from '../../types'

interface LibraryCommonProps {
  formikbag: FormikProps<FormProps>
  loading: boolean
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

interface LibraryItemProps extends LibraryCommonProps {
  library: Library
  odd: boolean
}

const LibraryItem = ({ library, odd, formikbag }: LibraryItemProps) => {
  const checked = formikbag.values.libraries.includes(library.identifier)
  const previewPic = library.version?.document?.previewFile?.url

  return (
    <LibraryItemBox odd={odd}>
      <Checkbox
        name="libraries"
        value={library.identifier}
        checked={checked}
        onChange={formikbag.handleChange}
      >
        <Flex alignItems="center">
          {previewPic && <LibraryPic src={previewPic} />}
          <Text
            fontWeight="medium"
            color="colors.foreground.secondary.A"
            fontSize="E"
          >
            {library.name}
          </Text>
        </Flex>
      </Checkbox>
    </LibraryItemBox>
  )
}

interface LibraryPanelProps extends LibraryCommonProps {
  libraries: Library[]
  search: string
  handleSearchUpdate: (value: string) => void
}

export const LibrariesPanel = ({
  libraries,
  formikbag,
  loading,
  errorLoad,
  search,
  handleSearchUpdate,
}: LibraryPanelProps & { errorLoad: boolean }) => {
  let content = <></>

  if (errorLoad) {
    content = (
      <Flex
        flexDirection="column"
        justifyContent="center"
        alignSelf="center"
        height="15vh"
        width="208px"
        pb={7}
      >
        <WarningTriangle />
        <Text
          color="foreground.secondary.A"
          fontSize="E"
          textAlign="center"
          mt={7}
          p={0}
          // necessary to override the padding-bottom 20 and margin 0 from Text
          style={{ padding: 0, marginTop: 20 }}
        >
          An error occurred
        </Text>
        <Text
          color="foreground.secondary.D"
          fontSize="D"
          textAlign="center"
          p={0}
        >
          An error occurred while getting the libraries, please try again.
        </Text>
      </Flex>
    )
  } else if (libraries.length === 0) {
    content = (
      <Flex
        flexDirection="column"
        justifyContent="center"
        alignSelf="center"
        height="15vh"
        width="208px"
        pb={7}
      >
        <SearchEmptyIcon />
        <Text
          color="foreground.secondary.A"
          fontSize="E"
          textAlign="center"
          mt={7}
          p={0}
          // necessary to override the padding-bottom 20 and margin 0 from Text
          style={{ padding: 0, marginTop: 20 }}
        >
          No search results
        </Text>
        <Text
          color="foreground.secondary.D"
          fontSize="D"
          textAlign="center"
          p={0}
        >
          No matching libraries found. Try again with another term.
        </Text>
      </Flex>
    )
  } else {
    content = (
      <>
        {libraries?.map((library, index) => (
          <LibraryItem
            key={library.identifier}
            library={library}
            loading={loading}
            formikbag={formikbag}
            onChange={formikbag.handleChange}
            odd={index % 2 !== 0}
          />
        ))}
      </>
    )
  }

  return (
    <>
      <StickyDiv>
        <Form.Field
          name="text"
          errorText={
            formikbag.touched.libraries ? formikbag.errors.libraries : undefined
          }
        >
          <SearchInput
            onChange={handleSearchUpdate}
            value={search}
            name="search"
            placeholder="Search Libraries"
          />
        </Form.Field>
      </StickyDiv>
      <LibrariesPanelBox>{content}</LibrariesPanelBox>
    </>
  )
}
